import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import styles from './DocPreview.module.css';
import { cx } from '../../lib/cx';
import { SignaturePad } from '../SignaturePad';
import { useActiveLogo } from '../../api/hooks';

export interface DocPageProps {
  company: {
    initials: string;
    name: string;
    address: string;
  };
  tag: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  footer?: {
    left?: ReactNode;
    center?: ReactNode;
    right?: ReactNode;
  };
  children: ReactNode;
}

/**
 * Bílý "papírový" list. Standardní hlavička, žlutý dělící pruh, obsah, patička.
 * Obsah je zároveň editovatelný formulář (DocRow s value/onChange).
 */
export function DocPage({ company, tag, title, meta, footer, children }: DocPageProps) {
  // Aktivní logo firmy (knihovna log). Na obrazovce nahradí iniciály v hlavičce,
  // v PDF (kde je tmavá hlavička skrytá) se vykreslí jako letterhead nad titulem.
  const { data: logo } = useActiveLogo();
  const logoUrl = logo?.data_url;

  return (
    <article className={styles.page} data-doc-page>
      <header className={styles.head}>
        <div className={cx(styles.headLogo, logoUrl && styles.headLogoImg)}>
          {logoUrl ? <img src={logoUrl} alt="Logo firmy" /> : company.initials}
        </div>
        <div className={styles.headText}>
          <div className={styles.headName}>{company.name}</div>
          <div className={styles.headAddr}>{company.address}</div>
        </div>
        <div className={styles.headTag}>{tag}</div>
      </header>

      <div className={styles.body}>
        {logoUrl && (
          <div className={styles.printLogo} aria-hidden>
            <img src={logoUrl} alt="Logo firmy" />
          </div>
        )}
        <div className={styles.docTitleWrap}>
          <div className={styles.docTitle}>{title}</div>
          {meta && <div className={styles.docMeta}>{meta}</div>}
        </div>

        {children}

        {footer && (
          <div className={styles.footer}>
            <span>{footer.left ?? ''}</span>
            <span>{footer.center ?? ''}</span>
            <span>{footer.right ?? ''}</span>
          </div>
        )}
      </div>
    </article>
  );
}

export interface DocBlockProps {
  idx: ReactNode;
  title: ReactNode;
  /** Pravostranný slot v hlavičce sekce (např. „+ Přidat účastníka"). Schovaný při tisku. */
  actions?: ReactNode;
  /** Počet skrytých řádků v této sekci. Pokud > 0, ukáže se „N skryto · obnovit". */
  hiddenCount?: number;
  /** Callback pro „obnovit všechny skryté řádky". */
  onRestoreHidden?: () => void;
  children: ReactNode;
}

export function DocBlock({
  idx,
  title,
  actions,
  hiddenCount,
  onRestoreHidden,
  children,
}: DocBlockProps) {
  const hasHidden = !!hiddenCount && hiddenCount > 0;
  return (
    <section className={styles.block}>
      <div className={styles.sectionLabel}>
        <span className={styles.sectionBadge}>{idx}</span>
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionLine} />
        {actions && (
          <span className={cx(styles.sectionActions, 'docNoPrint')}>{actions}</span>
        )}
      </div>
      <div className={styles.table}>{children}</div>
      {hasHidden && (
        <button
          type="button"
          className={cx(styles.restoreBtn, 'docNoPrint')}
          onClick={onRestoreHidden}
          title="Obnovit všechny skryté řádky"
        >
          ↺ {hiddenCount} skryto · obnovit
        </button>
      )}
    </section>
  );
}

/**
 * Dvě sekce vedle sebe (typicky Objednatel | Dodavatel, Investor | Zhotovitel).
 * Na širokém displeji i v tisku/PDF dva sloupce — ušetří zhruba třetinu strany;
 * na telefonu se sloupce skládají pod sebe. Používej pro dvojici krátkých
 * sekcí s podobným počtem řádků, ne pro dlouhé texty.
 */
export function DocBlockPair({ children }: { children: ReactNode }) {
  return <div className={styles.blockPair}>{children}</div>;
}

export interface DocRowProps {
  k: ReactNode;
  /** Read-only obsah (chips, QR, custom). Použij když řádek nemá jediný textový vstup. */
  v?: ReactNode;
  /** Inline editovatelná hodnota — pokud dodáno spolu s `onChange`, vykreslí se input. */
  value?: string;
  onChange?: (next: string) => void;
  placeholder?: string;
  mono?: boolean;
  multiline?: boolean;
  /** Akční tlačítka (Mapa, ARES, Adresář, QR…). Schovaná do hover/focus, vyloučená z tisku. */
  actions?: ReactNode;
  /** Pokud dodáno, vpravo v řádku přibude ✕ pro skrytí/odebrání řádku (jen UI, ne v PDF). */
  onRemove?: () => void;
  /** Tooltip pro ✕ tlačítko. */
  removeTitle?: string;
  /** Pokud true, input nezalomí na nový řádek (typicky pro úzké hodnoty). */
  inputType?: 'text' | 'email' | 'tel' | 'url';
  /** Počet řádků textarea (pouze pro multiline). Default 2. */
  rows?: number;
  /**
   * Jednořádková hodnota, která se ale smí ZALOMIT (název firmy, adresa, banka).
   * Popisek zůstává vlevo; místo `<input>` (který dlouhý text ořízne a zbytek
   * je vidět jen po kliknutí) se vykreslí textarea rostoucí s obsahem.
   * Enter se ignoruje, nové řádky se nevkládají. Výchozí pro `inputType`
   * 'text'; `wrap={false}` vrátí klasický jednořádkový input.
   */
  wrap?: boolean;
}

export function DocRow({
  k,
  v,
  value,
  onChange,
  placeholder,
  mono,
  multiline,
  actions,
  onRemove,
  removeTitle = 'Skrýt řádek',
  inputType = 'text',
  rows = 2,
  wrap: wrapProp,
}: DocRowProps) {
  // Textové hodnoty se zalamují vždy (adresa, název…), aby byl v aplikaci vidět
  // celý text jako v PDF. E-mail/telefon/URL zůstávají jako jednořádkový input.
  const wrap = wrapProp ?? inputType === 'text';
  const id = useId();
  const editable = typeof onChange === 'function';
  const hasRightChips = !!actions || !!onRemove;
  // Dlouhý text: řádek na celou šířku (bez popisku), textarea roste podle obsahu.
  const fullWidth = editable && !!multiline;

  const grows = multiline || wrap;
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el || !grows) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, grows]);
  useEffect(() => {
    if (!grows) return;
    function grow() {
      const el = taRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
    window.addEventListener('resize', grow);
    return () => window.removeEventListener('resize', grow);
  }, [grows]);

  return (
    <div className={cx(styles.row, fullWidth && styles.rowFull)}>
      {!fullWidth && (
        <div className={styles.rowKey}>
          {editable ? <label htmlFor={id}>{k}</label> : k}
        </div>
      )}
      <div
        className={cx(
          styles.rowValue,
          fullWidth && styles.rowValueFull,
          wrap && styles.rowValueWrap,
          mono && styles.mono,
        )}
      >
        {editable ? (
          multiline ? (
            <textarea
              ref={taRef}
              id={id}
              className={cx(styles.rowTextarea, mono && styles.mono)}
              value={value ?? ''}
              onChange={(e) => onChange!(e.target.value)}
              placeholder={placeholder}
              rows={rows}
            />
          ) : wrap ? (
            <textarea
              ref={taRef}
              id={id}
              className={cx(styles.rowTextarea, styles.rowTextareaInline, mono && styles.mono)}
              value={value ?? ''}
              onChange={(e) => onChange!(e.target.value.replace(/[\r\n]+/g, ' '))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              placeholder={placeholder}
              rows={1}
            />
          ) : (
            <input
              id={id}
              type={inputType}
              className={cx(styles.rowInput, mono && styles.mono)}
              value={value ?? ''}
              onChange={(e) => onChange!(e.target.value)}
              placeholder={placeholder}
            />
          )
        ) : (
          <span className={styles.rowValueText}>{v}</span>
        )}
        {hasRightChips && (
          <span className={cx(styles.rowActions, 'docNoPrint')}>
            {actions}
            {onRemove && (
              <button
                type="button"
                className={styles.rowRemove}
                onClick={onRemove}
                aria-label={removeTitle}
                title={removeTitle}
              >
                ✕
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export function DocChip({ children }: { children: ReactNode }) {
  return <span className={styles.chip}>✓ {children}</span>;
}

export interface DocSignature {
  label: string;
  name: string;
  role: string;
  /** Hodnota podpisu (data URL). Pokud `onSign` dodán, kreslí se inline pad přímo v sigBoxu. */
  image?: string;
  onSign?: (dataUrl: string) => void;
  /** Pokud dodáno, v rohu pole přibude ✕ pro odebrání (jen UI, ne v PDF). */
  onRemove?: () => void;
}

export function DocSignatures({ items }: { items: DocSignature[] }) {
  return (
    <div className={styles.signatures}>
      {items.map((s, i) => (
        <div key={`${s.label}-${i}`} className={styles.sigBox}>
          {s.onRemove && (
            <button
              type="button"
              className={cx(styles.sigRemove, 'docNoPrint')}
              onClick={s.onRemove}
              aria-label="Odebrat podpisové pole"
              title="Odebrat podpisové pole"
            >
              ✕
            </button>
          )}
          <div className={styles.sigLabel}>{s.label}</div>
          <div className={styles.sigName}>{s.name || '—'}</div>
          <div className={styles.sigRole}>{s.role}</div>
          {s.onSign ? (
            <div className={styles.sigPadWrap}>
              <SignaturePad value={s.image} onChange={s.onSign} />
            </div>
          ) : s.image ? (
            <div className={styles.sigImage}>
              <img src={s.image} alt={`Podpis — ${s.label}`} />
            </div>
          ) : (
            <div className={styles.sigLine}>✎ podepsat</div>
          )}
        </div>
      ))}
    </div>
  );
}
